import debounce from 'lodash.debounce';
import {
  FocusEvent,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Autosuggest, {
  ChangeEvent,
  RenderInputComponentProps,
  RenderSuggestionsContainerParams,
} from 'react-autosuggest';

import { SearchLocation } from 'algolia-api/types/SearchLocation';
import {
  InputAffixVariant,
  InputStyleVariant,
} from 'components/Input/Input.types';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { setGMapsError } from 'modules/main/store/ducks/gMapsError.actions';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { REMOTE_KEYWORDS } from 'utils/constants/search';

import { LocationInputAutosuggestWrapper } from './LocationInputAutosuggestWrapper';
import { LocationInputSuggestion } from './LocationInputSuggestion';
import { LocationSuggestion } from './LocationSuggestion';
import { StyledInput } from './StyledInput';
import { SuggestionsContainer } from './SuggestionsContainer';
import { getCity } from './getCity';
import { getPlacePredictions } from './getPlacePredictions';
import { getRemoteOptions } from './getRemoteOptions';

const defaultKeyword = REMOTE_KEYWORDS[CURRENT_LOCALE][0];

type Props = {
  onChange: (
    searchLocation: SearchLocation | null | undefined,
    shouldEnqueueSearch?: boolean,
    setRemote?: boolean,
  ) => void;
  onClear?: () => void;
  hasSuccess?: boolean;
  'data-qa-id'?: string;
  placeholder?: string;
  showLocationIcon?: boolean;
  showClearButton?: boolean;
  className?: string;
  handlePlaceDetails?: (place: google.maps.GeocoderResult) => void;
  addressTypes?: Array<string>;
  includeCity?: boolean;
  includeFull?: boolean;
  onBlur?: (event: FocusEvent<HTMLElement, Element>) => void;
  disabled?: boolean;
  hasRemoteOptions?: boolean;
  highlightFirstSuggestion?: boolean;
  componentRestrictions?: google.maps.places.ComponentRestrictions;
  affixVariant?: InputAffixVariant;
  styleVariant?: InputStyleVariant;
  locationText: string | undefined | null;
};

export function LocationInput({
  hasSuccess,
  onChange,
  onClear,
  placeholder,
  showLocationIcon = false,
  showClearButton,
  className,
  addressTypes,
  includeCity,
  includeFull,
  disabled,
  highlightFirstSuggestion = true,
  componentRestrictions,
  hasRemoteOptions,
  locationText,
  onBlur,
  handlePlaceDetails: handlePlaceDetailsProp,
  'data-qa-id': dataQaId,
  affixVariant,
  styleVariant,
}: Props) {
  const dispatch = useMainDispatch();

  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);

  // This is used to keep the input state if typed before the app has fully loaded
  const [backupState, setBackupState] = useState('');

  const gMapsErrored = useMainSelector((state) => state.gMapsError.errored);
  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService>();
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder>();
  const [sessionToken, setSessionToken] =
    useState<google.maps.places.AutocompleteSessionToken>();

  const [locationInputValue, setLocationInputValue] = useState(
    locationText || '',
  );
  const [prevPropsLocationText, setPrevPropsLocationText] =
    useState(locationText);

  useEffect(() => {
    if (locationText !== prevPropsLocationText) {
      setLocationInputValue(locationText || '');
      setPrevPropsLocationText(locationText);
    }
  }, [locationText, prevPropsLocationText]);

  const clearLocation = useCallback(() => {
    setLocationInputValue('');
    setBackupState('');

    if (onClear) onClear();

    onChange(undefined, false);
  }, [onChange, onClear]);

  const onLocationInputValueChange = (
    _: FormEvent<unknown>,
    { newValue }: ChangeEvent,
  ) => {
    if (newValue === '') {
      clearLocation();
    }

    setLocationInputValue(newValue);
  };

  const onBlurInternal = (e: FocusEvent<HTMLElement>) => {
    if (onBlur) {
      return onBlur(e);
    }

    if (locationInputValue && locationInputValue !== locationText) {
      clearLocation();
    }
  };

  const loadGoogleMapsAutocomplete = useCallback(async () => {
    if (autocompleteService) return;

    setLoading(true);

    try {
      const { AutocompleteService, AutocompleteSessionToken } =
        (await google.maps.importLibrary(
          'places',
        )) as google.maps.PlacesLibrary;
      const { Geocoder } = (await google.maps.importLibrary(
        'geocoding',
      )) as google.maps.GeocodingLibrary;
      setAutocompleteService(new AutocompleteService());
      setGeocoder(new Geocoder());
      setSessionToken(new AutocompleteSessionToken());
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setGMapsError(error.message));
      }
    }
  }, [autocompleteService, dispatch]);

  const handlePlaceDetails = useCallback(
    (place: google.maps.GeocoderResult) => {
      if (handlePlaceDetailsProp) {
        handlePlaceDetailsProp(place);
        return;
      }

      const addressComponents: Record<string, string> =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        place.address_components.reduce((hash: any, component: any) => {
          const attributeName = component.types[0];

          return {
            ...hash,
            [attributeName]: [
              'country',
              'administrative_area_level_1',
            ].includes(attributeName)
              ? component.short_name
              : component.long_name,
          };
        }, {});

      if (!('country' in addressComponents)) {
        // below: special handling of 'south east asia', 'gaza', etc.
        // i.e. results for which the autocomplete api does not return a country code
        // we will use the iso 3166 user-defined 'XA' code for this situation
        // https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#User-assigned_code_elements
        addressComponents.country = 'XA';
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-param-reassign
        place.types[0] = 'undefined_country_geocodes';
      }

      const countryCode = addressComponents.country;
      const stateCode = addressComponents.administrative_area_level_1;
      let city;
      let full;

      if (includeCity) {
        city = getCity(addressComponents);
      }

      if (includeFull) {
        full = place.formatted_address;
      }

      const boundingBox = place.geometry.viewport.toJSON();
      onChange(
        {
          text: place.formatted_address,
          stateCode,
          countryCode,
          boundingBoxN: boundingBox.north,
          boundingBoxW: boundingBox.west,
          boundingBoxS: boundingBox.south,
          boundingBoxE: boundingBox.east,
          geoType: place.types[0],
          // @ts-expect-error Can we use full?
          latLongAddress: place.formatted_address,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          full,
          city,
        },
        true,
        false,
      );
    },
    [handlePlaceDetailsProp, includeCity, includeFull, onChange],
  );

  const onSuggestionSelected = useCallback(
    async (
      _: FormEvent<HTMLInputElement>,
      { suggestion }: { suggestion: LocationSuggestion },
    ) => {
      if (!geocoder) return;

      if (suggestion.isRemoteOption) {
        onChange(
          {
            text: null,
            stateCode: null,
            countryCode: null,
            boundingBoxN: null,
            boundingBoxW: null,
            boundingBoxS: null,
            boundingBoxE: null,
            geoType: null,
            latitude: null,
            longitude: null,
          },
          true,
          true,
        );

        return;
      }

      await geocoder.geocode(
        {
          placeId: suggestion.place_id,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (results: any) => handlePlaceDetails(results[0]),
      );
    },
    [geocoder, handlePlaceDetails, onChange],
  );

  const [suggestions, setSuggestions] = useState<LocationSuggestion[] | null>(
    null,
  );

  useEffect(() => {
    if (
      inputRef.current &&
      document.activeElement === inputRef.current &&
      !autocompleteService
    ) {
      setBackupState(inputRef.current.value);
    }
  }, [autocompleteService]);

  const [inProgressQuery, setInProgressQuery] = useState<
    | Promise<
        ReadonlyArray<{
          name: string;
          value: string;
        }>
      >
    | null
    | undefined
  >(null);

  const loadSuggestions = useCallback(
    (value: string) => {
      // Cancel the previous request
      if (inProgressQuery) {
        inProgressQuery.then(() => {
          debounce(() => loadSuggestions(value), 200);
        });
        return undefined;
      }

      if (!autocompleteService || !sessionToken) return;

      const query = getPlacePredictions({
        autocompleteService,
        input: value,
        componentRestrictions,
        addressTypes,
        sessionToken,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const remoteOptions: any = [];

      if (hasRemoteOptions) {
        remoteOptions.push(getRemoteOptions({ value, defaultKeyword }));
      }

      query.then((options) => {
        // @ts-expect-error TS(2488): Type 'unknown' must have a '[Symbol.iterator]()' m... Remove this comment to see the full error message
        setSuggestions([...remoteOptions, ...options].filter(Boolean));
        setInProgressQuery(null);
        setLoading(false);
        if (backupState) {
          setLocationInputValue(backupState);
        }
      });

      // @ts-expect-error TS(2345): Argument of type 'Promise<unknown>' is not assigna... Remove this comment to see the full error message
      setInProgressQuery(query);
      return query;
    },
    [
      addressTypes,
      autocompleteService,
      backupState,
      componentRestrictions,
      hasRemoteOptions,
      inProgressQuery,
      sessionToken,
    ],
  );

  const onSuggestionsFetchRequested = useCallback(
    ({ value }: { value: string }) => {
      if (autocompleteService) {
        loadSuggestions(value);
      }
    },
    [autocompleteService, loadSuggestions],
  );

  const renderSuggestion = useCallback(
    (suggestion: LocationSuggestion) => (
      <LocationInputSuggestion suggestion={suggestion} />
    ),
    [],
  );

  const onSuggestionsClearRequested = useCallback(() => {
    if (suggestions) {
      setSuggestions([]);
    }
    setInProgressQuery(null);
  }, [suggestions]);

  useEffect(() => {
    if (
      !autocompleteService &&
      inputRef.current === document.activeElement &&
      backupState
    ) {
      loadGoogleMapsAutocomplete();
    } else if (
      inputRef.current === document.activeElement &&
      backupState &&
      !suggestions
    ) {
      loadSuggestions(backupState);
    } else if (
      inputRef.current === document.activeElement &&
      locationInputValue &&
      !suggestions
    ) {
      loadSuggestions(locationInputValue);
    }
  }, [
    autocompleteService,
    backupState,
    loadGoogleMapsAutocomplete,
    loadSuggestions,
    locationInputValue,
    suggestions,
  ]);

  const renderSuggestionsContainer = ({
    containerProps,
    children,
  }: RenderSuggestionsContainerParams) => {
    if (!children && (!loading || (loading && !locationInputValue)))
      return null;

    return (
      <SuggestionsContainer containerProps={containerProps} loading={loading}>
        {loading && locationInputValue ? <LoadingIndicator /> : children}
      </SuggestionsContainer>
    );
  };

  const renderInputComponent = useCallback(
    ({
      autoComplete,
      className: inputClassName,
      disabled: inputDisabled,
      onBlur: inputOnBlur,
      onChange: inputOnChange,
      onFocus,
      onKeyDown,
      onKeyPress,
      placeholder: inputPlaceholder,
      ref,
      title,
      type,
      value,
      ...renderInputComponentProps
    }: RenderInputComponentProps) => (
      <StyledInput
        aria-activedescendant={
          renderInputComponentProps['aria-activedescendant']
        }
        ref={ref}
        aria-autocomplete={renderInputComponentProps['aria-autocomplete']}
        aria-controls={renderInputComponentProps['aria-controls']}
        aria-label={getText('Location')}
        autoComplete={autoComplete}
        className={inputClassName}
        disabled={inputDisabled}
        onBlur={inputOnBlur}
        onChange={inputOnChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        hasSuccess={hasSuccess}
        onKeyPress={onKeyPress}
        placeholder={placeholder}
        title={title}
        value={value}
        onClear={showClearButton ? clearLocation : null}
        gMapsErrored={gMapsErrored}
        showLocationIcon={showLocationIcon}
        styleVariant={styleVariant}
        affixVariant={affixVariant}
        id={dataQaId || 'location-input'}
        data-qa-id={dataQaId || 'location-input'}
      />
    ),
    [
      clearLocation,
      gMapsErrored,
      hasSuccess,
      placeholder,
      dataQaId,
      affixVariant,
      styleVariant,
      showClearButton,
      showLocationIcon,
    ],
  );

  const onKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }, []);

  const getSuggestionValue = useCallback(
    (suggestion: LocationSuggestion) => suggestion.description,
    [],
  );

  return (
    <LocationInputAutosuggestWrapper $loading={loading}>
      <Autosuggest
        highlightFirstSuggestion={highlightFirstSuggestion}
        suggestions={suggestions || []}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={(v) =>
          Boolean(v && v.toString().trim().length >= 2)
        }
        renderInputComponent={renderInputComponent}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={{
          className,
          disabled,
          onChange: onLocationInputValueChange,
          onBlur: onBlurInternal,
          onFocus: loadGoogleMapsAutocomplete,
          onKeyPress,
          placeholder,
          value: locationInputValue || backupState || '',
          title: getText('Location'),
          ref: inputRef,
        }}
      />
    </LocationInputAutosuggestWrapper>
  );
}
